import { Component } from '@angular/core';

type CustomerType = 'home' | 'commercial';
type CalculationType = 'bill' | 'system';

@Component({
  selector: 'app-solar-calculator',
  templateUrl: './solar-calculator.component.html',
  styleUrls: ['./solar-calculator.component.scss']
})
export class SolarCalculatorComponent {

  customerType: CustomerType = 'home';
  calculationType: CalculationType = 'bill';
  monthlyBill: number | null = 1000;
  systemSize = 0;

  // CALCULATED RESULTS

  annualSavings = 0;
  annualEnergy = 0;
  roofSpace = 0;
  systemPrice = 0;
  subsidy = 0;
  solarCitiDiscount = 0;
  effectiveCost = 0;

  ngOnInit(): void {
    this.calculateSolar();
  }
  // SOLAR CALCULATOR CONFIGURATION

  private readonly CONFIG = {
    peakSunHours: 5,
    efficiency: 0.77,
    panelWattage: 550,
    roofAreaPerKw: 100,

    electricityRate: {
      home: 8,
      commercial: 10
    },
    installationCostPerWatt: {
      home: 55,
      commercial: 60
    },
    solarCitiDiscountPercentage: {
      home: 5,
      commercial: 3
    },
    minimumBill: 1,
    minimumSystemSize: 1,
    systemSizeStep: 0.5
  };

  // CUSTOMER TYPE
  selectCustomerType(type: CustomerType): void {
    this.customerType = type;
    // Recalculate automatically if user already entered data
    if (this.monthlyBill && this.monthlyBill > 0) {
      this.calculateSolar();
    }
  }

  // CALCULATION TYPE
  selectCalculationType(type: CalculationType): void {
    this.calculationType = type;
    if (type === 'bill') {
      if (this.monthlyBill && this.monthlyBill > 0) {
        this.calculateSolar();
      }
    } else {
      if (this.systemSize > 0) {
        this.calculateFromSystemSize();
      }
    }
  }

  // BILL INPUT CHANGE

  onBillChange(event: Event): void {

    const input = event.target as HTMLInputElement;

    // Maximum 8 digits
    if (input.value.length > 8) {
      input.value = input.value.slice(0, 8);
    }

    // Update Angular model
    this.monthlyBill = input.value
      ? Number(input.value)
      : null;

    // Calculate
    if (!this.monthlyBill || this.monthlyBill <= 0) {
      this.resetResults();
      return;
    }

    if (this.calculationType === 'bill') {
      this.calculateSolar();
    }
  }

  // SYSTEM SIZE INPUT CHANGE
  onSystemSizeChange(): void {
    if (!this.systemSize || this.systemSize <= 0) {
      this.resetResults();
      return;
    }
    if (this.calculationType === 'system') {
      this.calculateFromSystemSize();
    }
  }

  // MAIN CALCULATIO
  calculateSolar(): void {

    if (this.calculationType === 'system') {
      this.calculateFromSystemSize();
      return;
    }
    // Validation
    if (!this.monthlyBill || this.monthlyBill < this.CONFIG.minimumBill) {
      this.resetResults();
      return;
    }
    // 1. Convert Electricity Bill -> Monthly Units
    const electricityRate =
      this.CONFIG.electricityRate[this.customerType];

    const monthlyEnergy =
      this.monthlyBill / electricityRate;

    const dailyEnergy =
      monthlyEnergy / 30;

    const calculatedSystemSize =
      dailyEnergy /
      (
        this.CONFIG.peakSunHours *
        this.CONFIG.efficiency
      );


    this.systemSize =
      this.roundSystemSize(calculatedSystemSize);

    this.calculateResults(
      this.systemSize,
      monthlyEnergy
    );
  }


  private calculateFromSystemSize(): void {

    if (!this.systemSize || this.systemSize <= 0) {
      this.resetResults();
      return;
    }

    const monthlyEnergy =
      this.getEstimatedMonthlyConsumption();

    this.calculateResults(
      this.systemSize,
      monthlyEnergy
    );
  }

  private calculateResults(
    systemSize: number,
    monthlyEnergyConsumption: number
  ): void {

    const monthlyGeneration =
      systemSize *
      this.CONFIG.peakSunHours *
      this.CONFIG.efficiency *
      30;

    this.annualEnergy =
      monthlyGeneration * 12;


    const usableMonthlyGeneration =
      Math.min(
        monthlyGeneration,
        monthlyEnergyConsumption
      );


    const electricityRate =
      this.CONFIG.electricityRate[this.customerType];


    this.annualSavings =
      usableMonthlyGeneration *
      electricityRate *
      12;
    // System Price
    //
    // System Size × 1000 × Cost per Watt

    const costPerWatt =
      this.CONFIG.installationCostPerWatt[
      this.customerType
      ];


    this.systemPrice =
      systemSize *
      1000 *
      costPerWatt;
    // Government Subsidy

    this.subsidy =
      this.calculateSubsidy(systemSize);

    // SOLARCITI Discount
    const discountPercentage =
      this.CONFIG.solarCitiDiscountPercentage[
      this.customerType
      ];

    this.solarCitiDiscount =
      this.systemPrice *
      (discountPercentage / 100);

    // Effective Cost
    this.effectiveCost =
      Math.max(
        0,
        this.systemPrice -
        this.subsidy -
        this.solarCitiDiscount
      );

    // Roof Space
    this.roofSpace =
      systemSize *
      this.CONFIG.roofAreaPerKw;
  }


  private calculateSubsidy(systemSize: number): number {
    if (this.customerType !== 'home') {
      return 0;
    }

    let subsidy = 0;
    const firstTwoKw = Math.min(systemSize, 2);
    subsidy += firstTwoKw * 30000;
    if (systemSize > 2) {
      const additionalKw =
        Math.min(systemSize - 2, 1);
      subsidy += additionalKw * 18000;
    }

    // Maximum subsidy = ₹78,000
    return Math.min(subsidy, 78000);
  }

  // ESTIMATE MONTHLY CONSUMPTION FROM BILL
  private getEstimatedMonthlyConsumption(): number {
    if (!this.monthlyBill || this.monthlyBill <= 0) {
      return 0;
    }
    const electricityRate =
      this.CONFIG.electricityRate[this.customerType];
    return this.monthlyBill / electricityRate;
  }

  // ROUND SYSTEM SIZE
  private roundSystemSize(size: number): number {
    const step =
      this.CONFIG.systemSizeStep;
    const rounded =
      Math.ceil(size / step) * step;
    return Math.max(
      this.CONFIG.minimumSystemSize,
      Number(rounded.toFixed(1))
    );
  }

  // RESET
  private resetResults(): void {
    this.systemSize = 0;
    this.annualSavings = 0;
    this.annualEnergy = 0;
    this.roofSpace = 0;
    this.systemPrice = 0;
    this.subsidy = 0;
    this.solarCitiDiscount = 0;
    this.effectiveCost = 0;
  }


  // NUMBER FORMATTING
  formatNumber(value: number): string {
    if (!value || isNaN(value)) {
      return '0';
    }
    return value.toLocaleString('en-IN', {
      maximumFractionDigits: 1
    });
  }

  // CURRENCY FORMATTING
  formatCurrency(value: number): string {
    if (!value || isNaN(value)) {
      return '0';
    }
    return Math.round(value).toLocaleString('en-IN');
  }
}